import * as Data from 'effect/Data';
import * as Either from 'effect/Either';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import * as P from 'effect/Predicate';
import * as Str from 'effect/String';

const BIC_LENGTH = 8;
const BIC_BRANCH_CODE_LENGTH = 3;
const BIC_WITH_BRANCH_CODE_LENGTH = BIC_LENGTH + BIC_BRANCH_CODE_LENGTH;

const hasLength =
  (length: number): P.Predicate<string> =>
  (s) =>
    Str.length(s) === length;

const hasValidLength = P.or(
  hasLength(BIC_LENGTH),
  hasLength(BIC_WITH_BRANCH_CODE_LENGTH),
);

/**
 * BIC (Bank Identifier Code)
 *
 * @see https://www.iban.de/bic.html
 */
export interface Bic {
  readonly _tag: 'Bic';
  /** The bank code. */
  readonly bankCode: string;
  /** The country code. */
  readonly countryCode: string;
  /** The location code. */
  readonly locationCode: string;
  /** The branch code. */
  readonly bankBranchCode: O.Option<string>;
}
export const Bic = Data.tagged<Bic>('Bic');

export function fromString(bicString: string): Either.Either<Bic, string> {
  return Fn.pipe(
    bicString,
    Either.liftPredicate(
      hasValidLength,
      () =>
        `BIC must be ${BIC_LENGTH} or ${BIC_WITH_BRANCH_CODE_LENGTH} characters long`,
    ),
    Either.map(partsFromString),
    Either.map(Bic),
  );
}

function partsFromString(bicString: string) {
  const bankCode = Fn.pipe(bicString, Str.substring(0, 4));
  const countryCode = Fn.pipe(bicString, Str.substring(4, 6));
  const locationCode = Fn.pipe(bicString, Str.substring(6, 8));
  const bankBranchCode = Fn.pipe(
    bicString,
    Str.substring(8),
    O.liftPredicate(Str.isNonEmpty),
  );

  return { bankCode, countryCode, locationCode, bankBranchCode };
}

/**
 * BIC (Bank Identifier Code)
 *
 * @see https://www.iban.de/bic.html
 *
 * @deprecated Use {@link Bic} instead.
 */
export class LegacyBic {
  private _value: string;

  constructor(bic: string) {
    this._value = bic;
  }

  /**
   * The complete value
   */
  public get value(): string {
    return this._value;
  }

  /**
   * The bank code
   */
  public get bankCode(): string {
    return this._value.substring(0, 4);
  }

  /**
   * The country code
   */
  public get countryCode(): string {
    return this._value.substring(4, 6);
  }

  /**
   * The location code
   */
  public get locationCode(): string {
    return this._value.substring(6, 8);
  }

  /**
   * The branch code
   */
  public get bankBranchCode(): string {
    return this._value.substring(8);
  }
}
